import {
    Component,
    Input,
    ChangeDetectionStrategy,
    ViewEncapsulation,
    AfterContentInit,
    ChangeDetectorRef,
    ElementRef,
    OnChanges,
    SimpleChanges
} from '@angular/core';
import { MediaOrder } from './media-options';
import { Avatar } from '@core/models/common/avatar';
import { isObject } from '@core/utils/object';
import { isString } from '@core/utils/string';
import { Alignment as MediaAlignment } from '@core/models/common/alignment';

/**
 * Used to create a media component
 *
 * Sample implementation
 *
 * @example
 * <ihd-media (media)="user" heading?="Header text" media-order?="after" media-align?="bottom">
 *   <div media-body>
 *    <h2>This is the media body</h2>
 *     <p>Lorem Ipsum is simply dummy text of the printing and </p>
 *   </div>
 * </ihd-media>
 *
 * @export
 * @class MediaComponent
 * @implements {OnInit}
 */
@Component({
    selector: 'ihd-media',
    templateUrl: 'media.component.html',
    styleUrls: ['./media.component.scss'],
    host: {
        '[class.mod-align-right]': 'mediaOrder === MediaOrder.AFTER',
        'class': 'ihd-media c-media'
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class MediaComponent implements AfterContentInit, OnChanges {
    /**
     * User object to display/could be icon name
     * Pass media = user object when mediaType is avatar (default)
     * Pass media = icon name when mediaType is icon
     *
     * @type {*}@memberof MediaComponent
     */
    @Input() media: Avatar | string;

    @Input() imgField: string = 'avatar_url';

    /**
     * Text to be displayed as media heading
     *
     * @type {string}@memberof MediaComponent
     */
    @Input() heading?: string;

    @Input() mediaStyle?: any;

    /**
     * Input to align the media: middle | bottom | center.
     * Defaults to center
     *
     * @type {string}@memberof MediaComponent
     */
    @Input('media-align') mediaAlign?: string = MediaAlignment.MIDDLE;

    /**
     * Order of the media: should it appear before or after the body
     * Defaults to before
     *
     * @type {string}@memberof MediaComponent
     */
    @Input('media-order') mediaOrder?: string = MediaOrder.BEFORE;

    @Input('class')
    set classAttr(value) {
        this.renderStyles();
    }

    public MediaOrder = MediaOrder;
    public mediaClass: string;
    public showAvatar: boolean = true;

    private avatarMappings: {} = {
        'c-media--xxs': 'c-avatar--xxs',
        'c-media--xs': 'c-avatar--xs',
        'c-media--sm': 'c-avatar--sm',
        'c-media--md': 'c-avatar--md',
        'c-media--lg': 'c-avatar--lg',
    };

    constructor(private cdr: ChangeDetectorRef,
                private element: ElementRef) {
    }

    ngAfterContentInit() {
        this.renderStyles();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes && changes.media && changes.media.currentValue) {
            this.media = changes.media.currentValue;
            this.renderStyles();
        }
    }

    renderStyles() {
        this.mediaClass
          = this.mediaAlign === MediaAlignment.MIDDLE && 'mod-align-middle'
          || this.mediaAlign === MediaAlignment.BOTTOM && 'mod-align-bottom'
          || this.mediaAlign === MediaAlignment.TOP && 'mod-align-top'
          || '';

        if (isObject(this.media)) {
            this.showAvatar = true;
        } else if (isString(this.media)) {
            // display ihd-icon since we have icon name in media
            this.showAvatar = false;
        }

        const classList: any = this.element.nativeElement.classList;
        Object.keys(this.avatarMappings).forEach((iconClass) => {
            if (classList.contains(iconClass)) {
                this.mediaClass += ' ' + this.avatarMappings[iconClass];
            }
        });
        this.cdr.markForCheck();
    }
}
